import React, { useEffect, useMemo, useState } from "react";
import { useAuth } from "../AuthContext";
import { ChevronUp, ChevronDown, ListOrdered, GripVertical, RotateCcw, Loader2, Save } from "lucide-react";
import api, { errMsg } from "../api";
import { toast } from "sonner";
import { NAV_CATALOG_IDS, orderedNavItems } from "../layout/AppLayout";
import { t } from "../i18n";

/**
 * Lets the user re-order the sidebar nav items. The Dashboard tile is fixed at
 * the top and not part of the re-ordering set. Persists via /auth/me/nav-order.
 */
export default function NavOrderEditor() {
    const { lang, user, setUser } = useAuth();
    const initial = useMemo(() => orderedNavItems(user).map((x) => x.id), [user]);
    const [order, setOrder] = useState(initial);
    const [saving, setSaving] = useState(false);

    useEffect(() => { setOrder(initial); }, [initial]);

    const dirty = order.join("|") !== initial.join("|");
    const items = order
        .map((id) => orderedNavItems(user).find((it) => it.id === id))
        .filter(Boolean);

    const move = (idx, delta) => {
        const next = idx + delta;
        if (next < 0 || next >= order.length) return;
        const arr = [...order];
        [arr[idx], arr[next]] = [arr[next], arr[idx]];
        setOrder(arr);
    };

    const onDragStart = (e, idx) => {
        e.dataTransfer.effectAllowed = "move";
        e.dataTransfer.setData("text/plain", String(idx));
    };
    const onDragOver = (e) => { e.preventDefault(); e.dataTransfer.dropEffect = "move"; };
    const onDrop = (e, idx) => {
        e.preventDefault();
        const from = parseInt(e.dataTransfer.getData("text/plain"), 10);
        if (Number.isNaN(from) || from === idx) return;
        const arr = [...order];
        const [moved] = arr.splice(from, 1);
        arr.splice(idx, 0, moved);
        setOrder(arr);
    };

    const save = async () => {
        setSaving(true);
        try {
            const { data } = await api.put("/auth/me/nav-order", { nav_order: order });
            if (setUser) setUser((u) => ({ ...(u || {}), nav_order: data.nav_order }));
            toast.success(lang === "de" ? "Reihenfolge gespeichert" : "Order saved");
        } catch (e) {
            toast.error(errMsg(e, lang === "de" ? "Speichern fehlgeschlagen" : "Save failed"));
        } finally { setSaving(false); }
    };

    const reset = () => setOrder(NAV_CATALOG_IDS);

    return (
        <section className="p-5 bg-white border border-neutral-200 rounded-lg mb-4" data-testid="nav-order-section">
            <h2 className="font-display text-lg font-bold flex items-center gap-2 mb-1">
                <ListOrdered className="w-5 h-5" /> {lang === "de" ? "Menü-Reihenfolge" : "Menu order"}
            </h2>
            <p className="text-sm text-neutral-600 mb-4">
                {lang === "de"
                    ? "Ordne deine Seitenleiste neu — per Drag & Drop oder mit den Pfeilen. Die Übersicht bleibt immer oben."
                    : "Reorder your sidebar — drag & drop or use the arrows. Dashboard stays pinned on top."}
            </p>

            <div className="flex items-center gap-2 px-3 py-2 mb-2 text-xs text-neutral-500 bg-neutral-50 rounded border border-dashed border-neutral-200">
                {lang === "de" ? "Übersicht (fix oben)" : "Dashboard (always first)"}
            </div>

            <ul className="space-y-1" data-testid="nav-order-list">
                {items.map((item, idx) => {
                    const Icon = item.icon;
                    return (
                        <li
                            key={item.id}
                            draggable
                            onDragStart={(e) => onDragStart(e, idx)}
                            onDragOver={onDragOver}
                            onDrop={(e) => onDrop(e, idx)}
                            data-testid={`nav-order-row-${item.id}`}
                            className="group flex items-center gap-2 px-2.5 py-2 bg-white border border-neutral-200 rounded-md hover:border-brand-400/50 hover:bg-brand-50/30 cursor-grab active:cursor-grabbing transition"
                        >
                            <GripVertical className="w-4 h-4 text-neutral-400 group-hover:text-neutral-700" />
                            <Icon className="w-4 h-4 text-neutral-600 shrink-0" />
                            <span className="flex-1 text-sm font-medium">
                                {item.labelKey ? t(lang, item.labelKey)
                                    : typeof item.label === "string" ? item.label
                                    : (item.label?.[lang] || item.label?.en || item.id)}
                            </span>
                            <div className="flex items-center gap-0.5">
                                <button
                                    onClick={() => move(idx, -1)}
                                    disabled={idx === 0}
                                    className="p-1 rounded hover:bg-neutral-100 disabled:opacity-30 disabled:cursor-not-allowed"
                                    title={lang === "de" ? "Hoch" : "Up"}
                                    type="button"
                                    data-testid={`nav-order-up-${item.id}`}
                                >
                                    <ChevronUp className="w-3.5 h-3.5" />
                                </button>
                                <button
                                    onClick={() => move(idx, 1)}
                                    disabled={idx === items.length - 1}
                                    className="p-1 rounded hover:bg-neutral-100 disabled:opacity-30 disabled:cursor-not-allowed"
                                    title={lang === "de" ? "Runter" : "Down"}
                                    type="button"
                                    data-testid={`nav-order-down-${item.id}`}
                                >
                                    <ChevronDown className="w-3.5 h-3.5" />
                                </button>
                            </div>
                        </li>
                    );
                })}
            </ul>

            <div className="flex items-center justify-between gap-2 mt-4">
                <button
                    onClick={reset}
                    type="button"
                    className="inline-flex items-center gap-1.5 px-3 h-9 rounded-md border border-neutral-300 hover:bg-neutral-50 text-sm"
                    data-testid="nav-order-reset"
                >
                    <RotateCcw className="w-3.5 h-3.5" /> {lang === "de" ? "Standard" : "Default"}
                </button>
                <button
                    onClick={save}
                    disabled={!dirty || saving}
                    type="button"
                    className="inline-flex items-center gap-1.5 px-4 h-9 rounded-md bg-brand-600 text-white hover:bg-brand-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
                    data-testid="nav-order-save"
                >
                    {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                    {lang === "de" ? "Speichern" : "Save"}
                </button>
            </div>
        </section>
    );
}
